// src/pages/Feedback.js
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

const CATEGORIES = ["General", "Suggestion", "Bug Report", "Compliment", "Other"];
const MAX_LEN = 1000;
const MIN_LEN = 20;
const THROTTLE_SECONDS = 60; // prevent multiple submits quickly

export default function Feedback() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    category: "General",
    rating: 0,
    message: "",
    company: "", // honeypot (should stay empty)
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const charsLeft = MAX_LEN - (form.message?.length || 0);
  const canSubmit = useMemo(() => validate(form, setErrors, false), [form]);

  // simple email regex (good enough for UI)
  const emailOk = useMemo(
    () => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || ""),
    [form.email]
  );

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: name === "message" ? value.slice(0, MAX_LEN) : value,
    }));
  }

  function setRating(r) {
    setForm((f) => ({ ...f, rating: r }));
  }

  function validate(data, set, show = true) {
    const err = {};
    if (!data.name?.trim()) err.name = "Please enter your name.";
    if (!data.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      err.email = "Enter a valid email.";
    if ((data.message || "").trim().length < MIN_LEN)
      err.message = `Please write at least ${MIN_LEN} characters.`;
    if (data.company) err.company = "Spam detected.";
    if (show) set(err);
    return Object.keys(err).length === 0;
  }

  function throttleGuard() {
    const key = "feedback_last_submit";
    const last = Number(localStorage.getItem(key) || 0);
    const now = Date.now();
    if (now - last < THROTTLE_SECONDS * 1000) return false;
    localStorage.setItem(key, String(now));
    return true;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate(form, setErrors, true)) return;
    if (!throttleGuard()) {
      showToast("⏳ Please wait a minute before sending another feedback.", "warn");
      return;
    }
    setBusy(true);
    try {
      await addDoc(collection(db, "feedback"), {
        name: form.name.trim(),
        email: form.email.trim(),
        category: form.category,
        rating: Number(form.rating || 0),
        message: form.message.trim(),
        path: window?.location?.pathname || "/",
        userAgent: navigator?.userAgent || "",
        createdAt: serverTimestamp(),
      });

      setForm({
        name: "",
        email: "",
        category: "General",
        rating: 0,
        message: "",
        company: "",
      });
      setErrors({});
      showToast("✅ Thank you! Your feedback has been received.", "ok");
    } catch (err) {
      console.error("Feedback submit error:", err);
      showToast("❌ Failed to submit feedback. Please try again.", "error");
    } finally {
      setBusy(false);
    }
  }

  function showToast(msg, type = "ok") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-violet-50 to-indigo-100 py-12 px-4 md:px-10 lg:px-20">
      <div className="max-w-3xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-violet-700 to-indigo-600 mb-8"
        >
          💬 Submit Your Feedback
        </motion.h1>

        {/* Toast */}
        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`mb-4 rounded-xl px-4 py-3 text-sm shadow
                ${toast.type === "ok" ? "bg-green-50 text-green-800 border border-green-200" :
                  toast.type === "warn" ? "bg-yellow-50 text-yellow-900 border border-yellow-200" :
                  "bg-rose-50 text-rose-800 border border-rose-200"}`}
            >
              {toast.msg}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45 }}
          className="bg-white border border-violet-100 shadow-2xl rounded-2xl overflow-hidden"
        >
          {/* Top gradient bar */}
          <div className="h-2 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-indigo-600" />
          <div className="p-8 space-y-6">
            {/* Name + Email */}
            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Name"
                name="name"
                placeholder="Your full name"
                value={form.name}
                onChange={handleChange}
                error={errors.name}
                disabled={busy}
              />
              <Input
                type="email"
                label="Email"
                name="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                error={errors.email || (form.email && !emailOk ? "Invalid email" : "")}
                disabled={busy}
              />
            </div>

            {/* Category + Rating */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label text="Category" />
                <select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  disabled={busy}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="md:pl-2">
                <Label text="Rating (optional)" />
                <StarRating value={form.rating} onChange={setRating} disabled={busy} />
              </div>
            </div>

            {/* Message */}
            <div>
              <Label text="Message" />
              <textarea
                name="message"
                rows={5}
                value={form.message}
                onChange={handleChange}
                disabled={busy}
                placeholder={`Describe in at least ${MIN_LEN} characters…`}
                className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500
                ${errors.message ? "border-rose-400" : "border-gray-300"}`}
              />
              <div className="mt-1 flex justify-between text-xs">
                <span className={`${errors.message ? "text-rose-600" : "text-gray-500"}`}>
                  {errors.message || `Minimum ${MIN_LEN} characters`}
                </span>
                <span className={`${charsLeft < 0 ? "text-rose-600" : "text-gray-500"}`}>
                  {charsLeft} characters left
                </span>
              </div>
            </div>

            {/* Honeypot (hidden) */}
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={handleChange}
              className="hidden"
              autoComplete="off"
              tabIndex={-1}
            />

            {/* Submit */}
            <button
              type="submit"
              disabled={busy || !canSubmit}
              className={`w-full rounded-lg py-3 font-bold text-white shadow transition
                ${busy || !canSubmit
                  ? "bg-violet-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"}`}
            >
              {busy ? "Submitting…" : "Submit Feedback"}
            </button>

            {/* Subtle note */}
            <p className="text-[12px] text-gray-500 text-center">
              We never share your email. By submitting, you agree to be contacted regarding your feedback.
            </p>
          </div>
        </motion.form>
      </div>
    </div>
  );
}

/* ---------------- UI Bits ---------------- */

function Label({ text }) {
  return (
    <label className="block font-semibold text-gray-700 mb-1">{text}</label>
  );
}

function Input({ label, error, className = "", ...props }) {
  return (
    <div className={className}>
      <Label text={label} />
      <input
        {...props}
        className={`w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500
        ${error ? "border-rose-400" : "border-gray-300"}`}
      />
      {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}

function StarRating({ value = 0, onChange, disabled }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          className="group"
        >
          <svg
            viewBox="0 0 20 20"
            className={`h-7 w-7 transition
              ${n <= value ? "fill-yellow-400 stroke-yellow-500" : "fill-gray-200 stroke-gray-400"}
              group-hover:scale-105`}
          >
            <path
              strokeWidth="1"
              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.146 3.52a1 1 0 00.95.69h3.7c.969 0 1.371 1.24.588 1.81l-2.993 2.176a1 1 0 00-.364 1.118l1.146 3.52c.3.921-.755 1.688-1.538 1.118L10.95 14.91a1 1 0 00-1.176 0l-2.586 1.989c-.783.57-1.838-.197-1.539-1.118l1.146-3.52a1 1 0 00-.364-1.118L3.438 8.947c-.783-.57-.38-1.81.588-1.81h3.7a1 1 0 00.95-.69l1.146-3.52z"
            />
          </svg>
        </button>
      ))}
      <span className="ml-2 text-xs text-gray-500">{value ? `${value}/5` : "—"}</span>
    </div>
  );
}
